import React from 'react';

import { CONFIGURATOR_THEME_CATEGORIES } from '../constants';
import {
  ConfiguratorContextThemeArtOption,
  ConfiguratorContextThemeCategory,
  ConfiguratorContextThemeColorsOption,
  ConfiguratorContextThemeDesignOption,
  ConfiguratorThemeArtClickHandler,
  ConfiguratorThemeArtOption,
  ConfiguratorThemeCategory,
  ConfiguratorThemeCategoryClickHandler,
  ConfiguratorThemeColorsClickHandler,
  ConfiguratorThemeColorsOption,
  ConfiguratorThemeDesignClickHandler,
  ConfiguratorThemeDesignOption,
  Maybe,
} from '../types';
import { getInitialThemeCategory } from '../utils/state';
import { hasText } from '../utils/validators';

export function useThemeStep(
  configDesignOptions: ConfiguratorThemeDesignOption[],
  configArtOptions: ConfiguratorThemeArtOption[],
) {
  const [category, setCategory] = React.useState<ConfiguratorThemeCategory>(getInitialThemeCategory());

  const [designOption, setDesignOption] = React.useState<Maybe<ConfiguratorThemeDesignOption>>();
  const [designOptions, setDesignOptions] = React.useState<ConfiguratorThemeDesignOption[]>([]);

  const [colorsOption, setColorsOption] = React.useState<Maybe<ConfiguratorThemeColorsOption>>();
  const [colorsOptions, setColorsOptions] = React.useState<ConfiguratorThemeColorsOption[]>([]);

  const [artOption, setArtOption] = React.useState<Maybe<ConfiguratorThemeArtOption>>();
  const [artOptions, setArtOptions] = React.useState<ConfiguratorThemeArtOption[]>([]);

  const [casinoName, setCasinoName] = React.useState<Maybe<string>>();
  const [casinoNameError, setCasinoNameError] = React.useState<boolean>(false);

  React.useEffect(() => {
    setDesignOptions(configDesignOptions);

    if (configDesignOptions.length > 0) {
      setDesignOption(configDesignOptions[0]);
      setColorsOptions(configDesignOptions[0].colors);

      if (configDesignOptions[0].colors.length > 0) {
        setColorsOption(configDesignOptions[0].colors[0]);
      }
    }

    setArtOptions(configArtOptions);

    if (configArtOptions.length > 0) {
      setArtOption(configArtOptions[0]);
    }
  }, [configDesignOptions, configArtOptions]);

  const handleCasinoNameChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>((event) => {
    const value = event.target.value.trim();

    const isValid = hasText(value);

    const name = isValid ? value : undefined;
    const error = !isValid;

    setCasinoName(name);
    setCasinoNameError(error);
  }, []);

  const handleThemeCategoryClick = React.useCallback<ConfiguratorThemeCategoryClickHandler>((nextCategory) => {
    setCategory(nextCategory);
  }, []);

  const handleThemeDesignClick = React.useCallback<ConfiguratorThemeDesignClickHandler>(
    (nextDesignOption) => {
      setDesignOption(nextDesignOption);

      if (nextDesignOption.id !== designOption?.id) {
        setColorsOptions(nextDesignOption.colors);

        if (nextDesignOption.colors.length > 0) {
          setColorsOption(nextDesignOption.colors[0]);
        }
      }
    },
    [designOption?.id],
  );

  const handleThemeColorsClick = React.useCallback<ConfiguratorThemeColorsClickHandler>((nextColorsOption) => {
    setColorsOption(nextColorsOption);
  }, []);

  const handleThemeArtClick = React.useCallback<ConfiguratorThemeArtClickHandler>((nextArtOption) => {
    setArtOption(nextArtOption);
  }, []);

  const themeCategories = React.useMemo<ConfiguratorContextThemeCategory[]>(() => {
    return CONFIGURATOR_THEME_CATEGORIES.map((themeCategory) => ({
      ...themeCategory,
      active: themeCategory.id === category.id,
      onClick: () => handleThemeCategoryClick(themeCategory),
    }));
  }, [category, handleThemeCategoryClick]);

  const themeDesignOptions = React.useMemo<ConfiguratorContextThemeDesignOption[]>(() => {
    return designOptions.map((design) => ({
      ...design,
      active: design.id === designOption?.id,
      onClick: () => handleThemeDesignClick(design),
    }));
  }, [designOption, designOptions, handleThemeDesignClick]);

  const themeColorsOptions = React.useMemo<ConfiguratorContextThemeColorsOption[]>(() => {
    return colorsOptions.map((colors) => ({
      ...colors,
      active: colors.id === colorsOption?.id,
      onClick: () => handleThemeColorsClick(colors),
    }));
  }, [colorsOption, colorsOptions, handleThemeColorsClick]);

  const themeArtOptions = React.useMemo<ConfiguratorContextThemeArtOption[]>(() => {
    return artOptions.map((art) => ({
      ...art,
      active: art.id === artOption?.id,
      onClick: () => handleThemeArtClick(art),
    }));
  }, [artOption, artOptions, handleThemeArtClick]);

  return React.useMemo(
    () => ({
      themeCategory: category,
      themeCategories,
      themeDesignOption: designOption,
      themeDesignOptions,
      themeColorsOption: colorsOption,
      themeColorsOptions,
      themeArtOption: artOption,
      themeArtOptions,
      casinoName,
      casinoNameError,
      onCasinoNameChange: handleCasinoNameChange,
    }),
    [
      category,
      themeCategories,
      designOption,
      themeDesignOptions,
      colorsOption,
      themeColorsOptions,
      artOption,
      themeArtOptions,
      casinoName,
      casinoNameError,
      handleCasinoNameChange,
    ],
  );
}
